/**
 * InventoryItemSelector Component
 *
 * Multi-select modal for choosing inventory items to add to a recipe.
 * Items are displayed in collapsible category groups with stock levels.
 */

import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { cn } from '@/lib/utils'
import { Package, Loader2 } from 'lucide-react'

// Category display names
const CATEGORY_LABELS = {
  raw_material: 'Raw Materials',
  packaging: 'Packaging',
  sundry: 'Sundry',
  other: 'Other'
}

// Category order for display
const CATEGORY_ORDER = ['raw_material', 'packaging', 'sundry', 'other']

/**
 * Format stock level for display
 */
function formatStock(item) {
  if (!item.current_stock && item.current_stock !== 0) {
    return 'No stock data'
  }

  const stock = parseFloat(item.current_stock || 0)
  const unit = item.unit_of_measure || ''

  if (stock === 0) {
    return `0 ${unit} in stock`
  }

  // Format number nicely
  const formatted = stock >= 1000
    ? `${(stock / 1000).toFixed(1)}k`
    : stock % 1 === 0
      ? stock.toString()
      : stock.toFixed(1)

  return `${formatted} ${unit} in stock`
}

export default function InventoryItemSelector({
  open,
  onOpenChange,
  selectedItems = [],
  onAccept,
  onCancel
}) {
  // Local selection state (allows cancel to revert)
  const [localSelection, setLocalSelection] = useState(new Set(selectedItems))

  // Fetch inventory items
  const { allItems, isLoading, error } = useInventoryItems({ activeOnly: true })

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped = {}

    for (const category of CATEGORY_ORDER) {
      grouped[category] = []
    }

    for (const item of allItems) {
      const category = item.category || 'other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(item)
    }

    // Sort items within each category alphabetically
    for (const category of Object.keys(grouped)) {
      grouped[category].sort((a, b) => a.name.localeCompare(b.name))
    }

    return grouped
  }, [allItems])

  // Get categories that have items
  const categoriesWithItems = useMemo(() => {
    return CATEGORY_ORDER.filter(cat => itemsByCategory[cat]?.length > 0)
  }, [itemsByCategory])

  // Reset local selection when modal opens
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      setLocalSelection(new Set(selectedItems))
    }
    onOpenChange(isOpen)
  }

  // Toggle item selection
  const toggleItem = (itemId) => {
    setLocalSelection(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  // Handle accept
  const handleAccept = () => {
    onAccept(Array.from(localSelection))
  }

  // Handle cancel
  const handleCancel = () => {
    setLocalSelection(new Set(selectedItems))
    onCancel()
  }

  const selectionCount = localSelection.size

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Select Inventory Items
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <p>Failed to load inventory items</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
            </div>
          ) : allItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No inventory items found</p>
              <p className="text-sm mt-1">Add items in Inventory management first</p>
            </div>
          ) : (
            <Accordion
              type="multiple"
              defaultValue={categoriesWithItems}
              className="w-full"
            >
              {categoriesWithItems.map(category => (
                <AccordionItem key={category} value={category}>
                  <AccordionTrigger className="text-sm font-medium hover:no-underline">
                    <span className="flex items-center gap-2">
                      {CATEGORY_LABELS[category]}
                      <span className="text-xs text-muted-foreground font-normal">
                        ({itemsByCategory[category].length})
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1">
                      {itemsByCategory[category].map(item => {
                        const isSelected = localSelection.has(item.id)
                        const stockText = formatStock(item)
                        const isOutOfStock = (item.current_stock || 0) === 0

                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => toggleItem(item.id)}
                            className={cn(
                              "w-full flex items-center gap-3 p-2 rounded-md text-left transition-colors",
                              isSelected
                                ? "bg-primary/10 border border-primary/20"
                                : "hover:bg-muted/50"
                            )}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleItem(item.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <p className={cn(
                                "text-sm font-medium truncate",
                                isSelected && "text-primary"
                              )}>
                                {item.name}
                              </p>
                              <p className={cn(
                                "text-xs",
                                isOutOfStock
                                  ? "text-amber-600"
                                  : "text-muted-foreground"
                              )}>
                                {stockText}
                              </p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-muted-foreground">
              {selectionCount} item{selectionCount !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleAccept}>
                Accept
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
